import { ChevronRight12, ChevronRight16 } from '@mgonetwork/icons';
import { Heading, Text } from '@mgonetwork/ui';
import * as Collapsible from '@radix-ui/react-collapsible';
import clsx from 'clsx';
import { useState, type ReactNode, useEffect } from 'react';

import { Card, type CardProps } from '~/ui/Card';
import { Divider } from '~/ui/Divider';

type Size = 'md' | 'sm';

interface TransactionBlockCardHeaderProps {
	open: boolean;
	size: Size;
	title?: string | ReactNode;
	collapsible?: boolean;
	isShowChevronRight?: boolean;
}

function TransactionBlockCardHeader({
	open,
	size,
	title,
	collapsible,
	isShowChevronRight,
}: TransactionBlockCardHeaderProps) {
	if (!title) {
		return null;
	}

	const headerContent = (
		<div
			className={clsx(
				'flex w-full justify-between',
				open && size === 'md' && 'pb-6',
				open && size === 'sm' && 'pb-4.5',
			)}
		>
			{typeof title === 'string' ? (
				// <Heading variant={size === 'md' ? 'heading4/semibold' : 'heading6/semibold'} color="text-hero-dark">

				// </Heading>
				<div className="defined-bg-20 w-full rounded-[4px] p-5 text-hero-dark">{title}</div>
			) : (
				title
			)}
			{collapsible && !isShowChevronRight && (
				<ChevronRight16
					className={clsx('defined-logo-color cursor-pointer', open && 'rotate-90')}
				/>
			)}
		</div>
	);

	if (collapsible) {
		return (
			<Collapsible.Trigger asChild>
				<div className="cursor-pointer">{headerContent}</div>
			</Collapsible.Trigger>
		);
	}

	return <>{headerContent}</>;
}

interface TransactionBlockCardSectionProps {
	children: ReactNode;
	defaultOpen?: boolean;
	title?: string | ReactNode;
}

export function TransactionBlockCardSection({
	title,
	defaultOpen = true,
	children,
}: TransactionBlockCardSectionProps) {
	const [open, setOpen] = useState(defaultOpen);
	return (
		<Collapsible.Root open={open} onOpenChange={setOpen} className="flex w-full flex-col gap-6">
			{title && (
				<Collapsible.Trigger>
					<div className="flex items-center gap-2 text-hero-dark">
						{typeof title === 'string' ? (
							<Text color="text-hero-dark" variant="body/semibold">
								{title}
							</Text>
						) : (
							title
						)}
						<Divider color="grayBc3" />
						{/*text-gray-45*/}
						<ChevronRight12
							className={clsx('defined-text-bg13 h-4 w-4 cursor-pointer ', open && 'rotate-90')}
						/>
					</div>
				</Collapsible.Trigger>
			)}

			<Collapsible.Content>{children}</Collapsible.Content>
		</Collapsible.Root>
	);
}

export interface TransactionBlockCardProps extends Omit<CardProps, 'size'> {
	children: ReactNode;
	title?: string | ReactNode;
	footer?: ReactNode;
	collapsible?: boolean;
	size?: Size;
	initialClose?: boolean;
	isShowChevronRight?: boolean;
}

export function TransactionBlockCard({
	title,
	footer,
	collapsible,
	size = 'md',
	children,
	initialClose,
	isShowChevronRight,
	...cardProps
}: TransactionBlockCardProps) {
	const [open, setOpen] = useState(!initialClose);
	return (
		<div className="relative w-full">
			<Card rounded="2xl" border="gray45" bg="bgNone" spacing="none" {...cardProps}>
				<Collapsible.Root
					open={open}
					onOpenChange={setOpen}
					className={clsx(size === 'md' ? 'p-5' : 'px-4 py-4.5')}
					// className={clsx(size === 'md' ? 'px-6 py-7' : 'px-4 py-4.5')}
				>
					<TransactionBlockCardHeader
						open={open}
						size={size}
						title={title}
						collapsible={collapsible}
						isShowChevronRight={isShowChevronRight}
					/>

					<Collapsible.Content>{children}</Collapsible.Content>
				</Collapsible.Root>

				{footer && (
					<div className={clsx('mb-2 rounded-b-2xl py-2.5', size === 'md' ? 'px-6' : 'px-0')}>
						{footer}
					</div>
				)}
			</Card>
		</div>
	);
}
